import {Box, Button} from '@mui/material';
import {SxProps, Theme} from '@mui/material';
import {useCan, usePersona} from 'src/permissions/hooks';
import {FC, useEffect, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useLocation, useParams} from 'react-router-dom';
import {companyApi, employeeApi} from 'src/api';
import {useAppDispatch} from 'src/app/hooks';
import {store} from 'src/app/store';
import AMPModal from 'src/components/common/AMPModal';
import {EditIcon} from 'src/components/icons/EditIcon';
import {EmployeeForm} from 'src/components/forms/EmployeeForm';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {UpdateEmployeeRequest} from 'src/types/employee';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import {UserIdentityCard} from 'src/components/users/UserIdentityCard';
import {actionButtonSx} from 'src/components/common/actionButton.sx';
import {paymentModeLabel} from 'src/components/users/userProfile';
import {UserResetWrapper} from 'src/components/users/UserResetWrapper';
import {EmployeeInformation} from 'src/components/common/EmployeeInformation';
import {PowerConsumption} from 'src/components/common/PowerConsumption';

const pageSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    mt: 2.5,
};


export const EmployeeInfo: FC = () => {
    const persona = usePersona();
    const canReadReports = useCan('reports.read');
    const dispatch = useAppDispatch();
    const intl = useIntl();

    const {id} = useParams();

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const businessId = queryParams.get('businessId');
    const companyId = queryParams.get('companyId');

    const [showEditModal, setShowEditModal] = useState(false);

    const [triggerUpdateEmployee] = employeeApi.useUpdateEmployeeByIdMutation();

    const {
        data: employeeData,
        isFetching: isFetchingEmployeeData,
        isLoading: isLoadingEmployeeData,
        isError: isErrorEmployeeData,
        error: errorEmployeeData,
        refetch: refetchEmployee,
    } = employeeApi.useGetEmployeeByIdQuery({
        params: {
            employeeId: +(id ?? 0),
            businessId: +(businessId ?? 0),
            companyId: +(companyId ?? 0),
        },
    });
    const {
        data: companyData,
        isFetching: isFetchingCompanyData,
        isLoading: isLoadingCompanyData,
        isError: isErrorCompanyData,
        error: errorCompanyData,
    } = companyApi.useGetCompanyByIdQuery({
        param: {
            businessId: +(businessId ?? 0),
            companyId: +(companyId ?? 0),
        },
    });

    const employeeNameLabel = useMemo<string>(() => {
        if (!employeeData) {
            return '';
        }

        const firstName = employeeData?.firstName ?? '';
        const lastName = employeeData?.lastName ?? '';

        return `${firstName} ${lastName}`.trim();
    }, [employeeData]);


    const handleShowEditModal = (status: boolean) => () => setShowEditModal(status);
    const canUpdateEmployee = useCan('employees.update');

    const handleSubmit = async (values: Omit<UpdateEmployeeRequest, 'businessId' | 'companyId'>) => {
        if (!employeeData) return;

        const data: UpdateEmployeeRequest = {
            ...values,
            businessId: +(businessId ?? 0),
            companyId: +(companyId ?? 0),
        };

        try {
            dispatch(preloaderChangeStatus(true));

            await triggerUpdateEmployee({employeeId: +(id ?? 0), data})
                .unwrap();

            NotifySuccess(intl.formatMessage({id: 'employeeUpdatedSuccessfully'}));
            setShowEditModal(false);
            refetchEmployee();
        } catch (error: any) {
            error.status === 400 && NotifyError(intl.formatMessage({id: 'cannotUpdateEmployee'}));
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    useEffect(() => {
        if (
            isFetchingEmployeeData ||
            isFetchingCompanyData ||
            isLoadingEmployeeData ||
            isLoadingCompanyData
        ) {
            store.dispatch(preloaderChangeStatus(true));
        } else {
            store.dispatch(preloaderChangeStatus(false));
        }
    }, [
        isFetchingEmployeeData,
        isFetchingCompanyData,
        isLoadingEmployeeData,
        isLoadingCompanyData,
    ]);

    useEffect(() => {
        if (!isErrorEmployeeData && !isErrorCompanyData) {
            return;
        }

        if (isErrorObject(errorEmployeeData)) {
            NotifyError(errorEmployeeData.data.message);
        }

        if (isErrorObject(errorCompanyData)) {
            NotifyError(errorCompanyData.data.message);
        }
    }, [
        isErrorEmployeeData,
        isErrorCompanyData,
        errorEmployeeData,
        errorCompanyData,
    ]);

    return (
        <Box sx={pageSx}>
            {employeeData && (
                <UserIdentityCard
                    data={{
                        avatar: employeeData.user?.avatar,
                        emailAddress: employeeData.user?.emailAddress,
                        phoneNumber: employeeData.user?.phoneNumber,
                    }}
                    title={employeeNameLabel}
                    typeLabel="EMPLOYEE"
                    facts={[
                        {label: 'createNewCompanyTab', value: companyData?.name},
                        {
                            label: 'paymentModeColumnHeader',
                            value: paymentModeLabel(employeeData.consumptionPaymentMode),
                        },
                        {label: 'homeAddress', value: undefined, emptyLabel: 'notSet'},
                        {label: 'registered', value: employeeData.createdAt
                            ? new Date(employeeData.createdAt).toLocaleDateString('fr-CH')
                            : undefined},
                    ]}
                    actions={(
                        <>
                            {canUpdateEmployee && (
                                <Button
                                    variant="contained"
                                    sx={actionButtonSx('32px', 'secondary')}
                                    startIcon={<EditIcon/>}
                                    onClick={handleShowEditModal(true)}
                                >
                                    <FormattedMessage id="edit"/>
                                </Button>
                            )}
                            <UserResetWrapper
                                data={employeeData}
                                toolbar
                            />
                        </>
                    )}
                />
            )}

            {employeeData && (
                <EmployeeInformation
                    employeeData={employeeData}
                    businessId={+(businessId ?? 0)}
                    companyId={+(companyId ?? 0)}
                    refetchEmployee={refetchEmployee}
                />
            )}

            {canReadReports && employeeData && (
                <PowerConsumption userId={employeeData.user?.id}/>
            )}

            {employeeData && (
                <AMPModal
                    handleClose={handleShowEditModal(false)}
                    open={showEditModal}
                    title={intl.formatMessage({id: 'editEmployee'})}
                    type="md"
                    fullWidth
                    withoutPaddings
                >
                    <EmployeeForm
                        handleClose={handleShowEditModal(false)}
                        isEditMode
                        initialValues={{
                            firstName: employeeData.firstName,
                            lastName: employeeData.lastName,
                            title: employeeData.title,
                            consumptionPaymentMode: employeeData.consumptionPaymentMode,
                            language: employeeData.user?.language,
                        }}
                        onSubmit={handleSubmit}
                        onCancel={handleShowEditModal(false)}
                        hidePaymentMode={persona.isEmployee}
                    />
                </AMPModal>
            )}
        </Box>
    );
};
